/**
 * Dialog para editar documentos usando TanStack Form
 * Migrado desde el patrón anterior de useState
 */
import { useCallback, useEffect } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDiscardGuard } from '@/components/ui/form-dialog-content'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useActualizarDocumento } from '@/hooks/useDocumentos'
import { toast } from 'sonner'
import { handleMutationError } from '@/lib/permisos-guard'
import type { Documento, CategoriaDocumento } from '@/types/database'
import { getCategoriasOptions } from '@/types/documentos'
import { Loader2 } from 'lucide-react'

// Schema de validación
const editarDocumentoSchema = z.object({
  nombre: z.string().min(1, 'El nombre es requerido'),
  categoria: z.string().min(1, 'La categoría es requerida'),
  fecha_vencimiento: z.string().nullable().optional(),
})

type EditarDocumentoFormValues = z.infer<typeof editarDocumentoSchema>

interface EditarDocumentoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documento: Documento | null
  onSuccess?: () => void
}

// Usar solo categorías del workspace contable (excluye jurídicas)
const categoriaOptions = getCategoriasOptions('contable')

/**
 * Helper para extraer mensaje de error de TanStack Form
 */
function getFieldError(errors: unknown[]): string | undefined {
  const first = errors[0]
  if (!first) return undefined
  if (typeof first === 'string') return first
  if (first && typeof first === 'object' && 'message' in first) {
    return (first as { message: string }).message
  }
  return undefined
}

export function EditarDocumentoDialog({
  open,
  onOpenChange,
  documento,
  onSuccess,
}: EditarDocumentoDialogProps) {
  const actualizarDocumento = useActualizarDocumento()

  // TanStack Form
  const form = useForm({
    defaultValues: {
      nombre: '',
      categoria: 'otros',
      fecha_vencimiento: null as string | null,
    } as EditarDocumentoFormValues,
    validators: {
      onSubmit: editarDocumentoSchema,
    },
  })

  // Sincronizar con documento cuando cambia
  useEffect(() => {
    if (documento && open) {
      form.reset(
        {
          nombre: documento.nombre || '',
          categoria: documento.categoria || 'otros',
          fecha_vencimiento: documento.fecha_vencimiento || null,
        },
        { keepDefaultValues: true },
      )
    }
  }, [documento, open, form])

  const isDirty = useStore(form.store, (s) => s.isDirty)

  const handleClose = useCallback(() => {
    form.reset()
    onOpenChange(false)
  }, [form, onOpenChange])

  const { holdClose, confirmation } = useDiscardGuard({ isDirty, onDiscard: handleClose })

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) return onOpenChange(true)
    if (!holdClose()) handleClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!documento) return

    const values = form.state.values

    if (!values.nombre.trim()) {
      toast.error('El nombre es requerido')
      return
    }

    try {
      await actualizarDocumento.mutateAsync({
        id: documento.id,
        data: {
          nombre: values.nombre.trim(),
          categoria: values.categoria as CategoriaDocumento,
          fecha_vencimiento: values.fecha_vencimiento || null,
        },
      })

      toast.success('Documento actualizado')
      handleClose()
      onSuccess?.()
    } catch (error) {
      handleMutationError(error, 'Error al actualizar el documento')
    }
  }

  if (!documento) return null

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Documento</DialogTitle>
            <DialogDescription>
              Modifica los datos del documento "{documento.nombre_original}"
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombre */}
            <form.Field name="nombre">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder="Nombre del documento"
                    disabled={actualizarDocumento.isPending}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Categoría */}
            <form.Field name="categoria">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="categoria">Categoría</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    disabled={actualizarDocumento.isPending}
                  >
                    <SelectTrigger id="categoria">
                      <SelectValue placeholder="Selecciona una categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriaOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {getFieldError(field.state.meta.errors)}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Fecha de vencimiento */}
            <form.Field name="fecha_vencimiento">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="fechaVencimiento">Fecha de vencimiento (opcional)</Label>
                  <Input
                    id="fechaVencimiento"
                    type="date"
                    value={field.state.value || ''}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    onBlur={field.handleBlur}
                    disabled={actualizarDocumento.isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    Útil para poderes, contratos y constancias con fecha de expiración
                  </p>
                </div>
              )}
            </form.Field>

            {/* Info del archivo original */}
            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
              <p>
                <strong>Archivo original:</strong> {documento.nombre_original}
              </p>
              <p>
                <strong>Subido:</strong>{' '}
                {documento.created_at
                  ? new Date(documento.created_at).toLocaleDateString('es-AR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })
                  : '-'}
              </p>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={actualizarDocumento.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={actualizarDocumento.isPending}>
                {actualizarDocumento.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar cambios'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {confirmation}
    </>
  )
}
